/**
 * Minimal, dependency-free ZIP writer (STORE method, no compression).
 *
 * Produces a standard .zip (local file headers + central directory + EOCD) that
 * macOS/Windows/Linux unzip natively. Font files (.ttf/.otf) are already dense,
 * so skipping compression keeps this tiny and avoids pulling in a zip library.
 *
 * Limitations: no Zip64 (fine for our few-MB font bundles), stores entries
 * uncompressed.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function toBytes(data) {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return new TextEncoder().encode(String(data));
}

/**
 * @param {Array<{name: string, data: Uint8Array|ArrayBuffer|string}>} entries
 * @returns {Blob} a zip archive as an image/zip Blob
 */
export function createZip(entries) {
  const encoder = new TextEncoder();
  const records = entries.map((entry) => {
    const nameBytes = encoder.encode(entry.name);
    const dataBytes = toBytes(entry.data);
    return { nameBytes, dataBytes, crc: crc32(dataBytes), offset: 0 };
  });

  const localParts = [];
  let offset = 0;

  // Local file headers + data
  for (const rec of records) {
    rec.offset = offset;
    const header = new DataView(new ArrayBuffer(30));
    header.setUint32(0, 0x04034b50, true); // local file header signature
    header.setUint16(4, 20, true); // version needed
    header.setUint16(6, 0, true); // flags
    header.setUint16(8, 0, true); // compression method = store
    header.setUint16(10, 0, true); // mod time
    header.setUint16(12, 0x21, true); // mod date (1980-01-01)
    header.setUint32(14, rec.crc, true);
    header.setUint32(18, rec.dataBytes.length, true); // compressed size
    header.setUint32(22, rec.dataBytes.length, true); // uncompressed size
    header.setUint16(26, rec.nameBytes.length, true);
    header.setUint16(28, 0, true); // extra length
    localParts.push(new Uint8Array(header.buffer), rec.nameBytes, rec.dataBytes);
    offset += 30 + rec.nameBytes.length + rec.dataBytes.length;
  }

  // Central directory
  const centralParts = [];
  let centralSize = 0;
  for (const rec of records) {
    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true); // central dir signature
    cd.setUint16(4, 20, true); // version made by
    cd.setUint16(6, 20, true); // version needed
    cd.setUint16(8, 0, true); // flags
    cd.setUint16(10, 0, true); // compression = store
    cd.setUint16(12, 0, true); // mod time
    cd.setUint16(14, 0x21, true); // mod date
    cd.setUint32(16, rec.crc, true);
    cd.setUint32(20, rec.dataBytes.length, true);
    cd.setUint32(24, rec.dataBytes.length, true);
    cd.setUint16(28, rec.nameBytes.length, true);
    cd.setUint16(30, 0, true); // extra length
    cd.setUint16(32, 0, true); // comment length
    cd.setUint16(34, 0, true); // disk number
    cd.setUint16(36, 0, true); // internal attrs
    cd.setUint32(38, 0, true); // external attrs
    cd.setUint32(42, rec.offset, true); // local header offset
    centralParts.push(new Uint8Array(cd.buffer), rec.nameBytes);
    centralSize += 46 + rec.nameBytes.length;
  }

  // End of central directory
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(4, 0, true);
  eocd.setUint16(6, 0, true);
  eocd.setUint16(8, records.length, true);
  eocd.setUint16(10, records.length, true);
  eocd.setUint32(12, centralSize, true);
  eocd.setUint32(16, offset, true); // central dir offset
  eocd.setUint16(20, 0, true); // comment length

  return new Blob([...localParts, ...centralParts, new Uint8Array(eocd.buffer)], {
    type: 'application/zip',
  });
}
